import profileApi from "@apis/profile.api.ts";
import { isDefined, isEmpty } from "@utils/var.check.ts";
import appConfigs from "@configs/app.ts";
import { redirect, removeLocalStorageItems } from "@plugins/router";
import ChangeTeacherAccountOption from "@modules/auth/components/ChangeTeacherAccountOption.vue";
import AuthApi from "@apis/auth.api.ts";
import AuthService from "@/modules/auth/service/auth.service";
import { cookieManager } from "@utils/cookie.manager.ts";
import ChangeTeacherAccountModal from "@modules/auth/components/ChangeTeacherAccountModal.vue";
import { useStore } from "vuex";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { computed } from "vue";
import { NAMESPACE } from "@modules/auth/service/login.constant.ts";
import { authStateChannel } from "@/modules/auth/service/auth.state.channel";

interface ChangeTeacherAccountModalParam {
  validated: boolean;
  password?: string;
}

export function useSwitchAccount() {
  const store = useStore();
  const modal = useModal();

  const userDisplayName = computed(() => store.getters[`${NAMESPACE.AUTH}/userDisplayName`]);

  async function switchAccount(
    password?: string,
    groupCollectionUuid?: string,
    loginFlow?: string,
  ) {
    try {
      const requestData = {
        password,
        loginFlow,
      };

      const response = await profileApi.switchAccount(requestData);
      removeLocalStorageItems();

      authStateChannel.switchAccount(); // Notify other tabs

      if (!isEmpty(response?.data?.redirectUrl)) {
        redirect(response.data.redirectUrl);
      } else if (isDefined(groupCollectionUuid)) {
        redirect(`${appConfigs.vleDomain}/class-group/view/${groupCollectionUuid}`);
      } else {
        redirect((window.location.href = appConfigs.vleDomain));
      }
    } catch (error) {
      console.error("Error switching account:", error);
      throw error;
    }
  }

  async function showAccountChangeOption(groupCollectionUuid?: string, loginFlow?: string) {
    modal.show(ChangeTeacherAccountOption, {
      actionSecondary: async () => {
        const resolve = await showAccountChangeModal();
        if (resolve.validated) {
          await switchAccount(resolve.password, groupCollectionUuid, loginFlow);
        }
      },
      actionPrimary: async (response) => {
        if (!response.validated) return;

        try {
          await AuthService.logout();
          removeLocalStorageItems();

          const edupassLoginResponse = await AuthApi.edupassLogin(loginFlow);
          if (edupassLoginResponse.code !== "ok") return;

          if (isDefined(groupCollectionUuid)) {
            const savedRequestUrl = `${appConfigs.vleDomain}/class-group/view/${groupCollectionUuid}`;
            cookieManager.createCookie("SAVED_REQUEST_URL", savedRequestUrl, null);
          }

          authStateChannel.switchAccount(); // Notify other tabs

          redirect(edupassLoginResponse.data);
        } catch (error) {
          console.error("Error switching account:", error);
        }
      },
    });
  }

  function showAccountChangeModal() {
    return new Promise<ChangeTeacherAccountModalParam>((resolve) => {
      modal.show(ChangeTeacherAccountModal, {
        userDisplayName,
        actionPrimary: (response: ChangeTeacherAccountModalParam) => resolve(response),
        actionSecondary: (response: ChangeTeacherAccountModalParam) => resolve(response),
      });
    });
  }

  return {
    switchAccount,
    showAccountChangeOption,
  };
}
